import React, {useEffect, useState} from "react";
import {Checkbox, Form, Input, Switch, Table, Button} from "antd";
import {shiftOptionTypes} from "./ShiftOptionTypes";
import {useStore} from "../../../../hooks/useStore";
import {useTranslation} from "react-i18next";
import {FormikProvider, getIn, useFormik} from "formik";
import {ErrorFocus} from "../../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CompanyButtonContainer} from "../optionsStyles";
import {handleCheckBoxText} from "../../../../utils/helpers";
import {shiftOptionsValidation} from "../shiftOptionsValidation";
import {EmptyMessage} from "../../../_defaultComponents/EmptyMessage/EmptyMessage";

const Options = ({
    companyLoading,
    setCompanyLoading,
    companyData,
    searchText,
    city,
    upperGroup,
    regionGroups,
    setSubCompanyRecall,
    setCompanyRecall,
    regionGroupsNames,
    companyRecallRef,
    subCompanyRecallRef
}: shiftOptionTypes) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const shift = companyData.data?.options?.shift || {};

    const formik = useFormik({
        initialValues: {
            options: {
                shift: {
                    shiftControlIsActive: shift.shiftControlIsActive || false,
                    earliestShiftStart: shift.earliestShiftStart || 1,
                    latestShiftEnd: shift.latestShiftEnd || 1,
                    distanceAlertIsActive: shift.distanceAlertIsActive || false,
                    distanceLimit: shift.distanceLimit || 1,
                    maxShiftPackageLimit: shift.maxShiftPackageLimit || 1
                },
                ...companyData.data?.options
            },
            applyShiftOptions: false,
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        validationSchema: shiftOptionsValidation,
        onSubmit: async values => {
            try {
                setCompanyLoading(true);

                let filters = {};
                filters.searchText = searchText;
                filters.city = city;
                filters.upperGroup = upperGroup;
                filters.regionGroups = regionGroups;
                if (Object.keys(filters).length > 0) {
                    values.filters = filters;
                }
                if (values.isActive) {
                    values.applyShiftOptions = true;
                }

                await stores.companyStore.updateCompanyDistanceNotificationOptions(values);
                if (values.isActive) {
                    setSubCompanyRecall(!subCompanyRecallRef.current);
                }
                setCompanyRecall(!companyRecallRef.current);
                setCompanyLoading(false);
            } catch (e) {
                console.log(e);
            }
        }
    });

    const {values, errors, touched, handleSubmit, isSubmitting, setFieldValue} = formik;
    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const renderSwitch = key => {
        return (
            <Table.Column
                title={`${t(`fields.shiftOptions.${key}.label`, {
                    ns: "common"
                })} (${t(`fields.shiftOptions.${key}.short`, {ns: "common"})})`}
                dataIndex="isActive"
                render={() => (
                    <Form.Item>
                        <Switch
                            checked={values.options.shift && values.options.shift[key]}
                            checkedChildren={t("other.active", {ns: "common"})}
                            unCheckedChildren={t("other.passive", {ns: "common"})}
                            data-testid={key}
                            onChange={e => {
                                setFieldValue(`options.shift.${key}`, e);
                                setIsCompanyFormChanged(true);
                            }}
                        />
                    </Form.Item>
                )}
                align="center"
            />
        );
    };

    const renderInput = (key, disableKey) => {
        const getInErrors = getIn(errors, `options.shift.${key}`);
        const getInTouch = getIn(touched, `options.shift.${key}`);
        const hasError = getInErrors && getInTouch;
        return (
            <Table.Column
                title={`${t(`fields.shiftOptions.${key}.label`, {
                    ns: "common"
                })} (${t(`fields.shiftOptions.${key}.short`, {ns: "common"})})`}
                dataIndex="limit"
                render={() => (
                    <Form.Item
                        validateStatus={hasError ? "error" : "success"}
                        help={hasError ? getInErrors : null}
                    >
                        <Input
                            type="number"
                            name="limit"
                            data-testid="limitInput"
                            value={
                                values.options.shift && values.options.shift[key]
                                    ? values.options.shift[key]
                                    : shift && shift[key]
                            }
                            onChange={e =>
                                setFieldValue(`options.shift.${key}`, e.target.value || "")
                            }
                            disabled={!values.options.shift || !values.options.shift[disableKey]}
                        />
                    </Form.Item>
                )}
                align="center"
            />
        );
    };

    return (
        <Form
            autoComplete="off"
            onFinish={handleSubmit}
            data-testid="appointmentOptionsForm"
            onChange={() => setIsCompanyFormChanged(true)}
        >
            <Table
                loading={companyLoading}
                dataSource={[companyData.data]}
                rowKey="_id"
                pagination={false}
                data-testid="appointmentOptionsTable"
                locale={{
                    emptyText: <EmptyMessage />
                }}
            >
                <ErrorFocus formik={formik} />
                <FormikProvider value={formik}>
                    {renderSwitch("shiftControlIsActive")}
                    {renderInput("earliestShiftStart", "shiftControlIsActive")}
                    {renderInput("latestShiftEnd", "shiftControlIsActive")}
                    {renderSwitch("distanceAlertIsActive")}
                    {renderInput("distanceLimit", "distanceAlertIsActive")}
                    {renderSwitch("packageLimitAlertIsActive")}
                    {renderInput("maxShiftPackageLimit", "packageLimitAlertIsActive")}
                </FormikProvider>
            </Table>
            <CompanyButtonContainer justify="end" align="middle">
                <Checkbox
                    data-testid="applyForAllSubCompaniesCheckbox"
                    checked={values?.applyShiftOptions}
                    onChange={e => setFieldValue("applyShiftOptions", e.target.checked)}
                >
                    {handleCheckBoxText(upperGroup, regionGroupsNames, searchText, city)}
                </Checkbox>
                <Button
                    htmlType="submit"
                    disabled={!isCompanyFormChanged || isSubmitting}
                    type="primary"
                    data-testid="submitButton"
                >
                    {t("actions.save", {ns: "common"})}
                </Button>
            </CompanyButtonContainer>
        </Form>
    );
};

export default Options;
